import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Badge, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import API from "../../common/API";

interface ParteItem {
    id?: number;
    nombre?: string;
    rol?: string;
    identificador?: string | null;
    cedulaMatchStatus?: number; // 0 Pending,1 Matched,2 Ambiguous,3 NotMatched,4 Manual,5 MatchedEntity,6 AmbiguousEntity,7 PreAssigned
    cedulaMatchCandidates?: number | null;
}

interface AudienciaData {
    linea: number;
    noExpediente: string;
    noUnico: string;
    idTribunal: string;
    descTribunal: string;
    idMateria: string;
    descMateria: string;
    fechaAudiencia: string;
    horaAudiencia: string;
    tipoAudiencia: string;
    estadoAudiencia: string;
    involucrados: string;
    partes?: string;
    partesList?: ParteItem[];
    sala: string;
    juez: string;
    totalFilas: number;
    origen: number;
    activo: boolean;
}

interface AudienciasResponse {
    draw: string;
    recordsFiltered: number;
    recordsTotal: number;
    data: AudienciaData[];
}

const RecordAudiencias = (props: any) => {
    const { id } = props;
    const [records, setRecords] = useState<AudienciaData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const [viewMode, setViewMode] = useState<"table" | "conversation">("conversation");

    const bindRecords = async () => {
        try {
            setIsLoading(true);
            const response = await API.getAction(
                `Busqueda/Audiences?identifier=${id}&name=${encodeURIComponent(props.name || '')}&start=0&length=100`
            );

            if (response.status) {
                const data: any = response.data;
                const rows: AudienciaData[] = data.datos || [];
                setRecords(rows);
                const hasLaboral = rows.some(r => (r.descMateria || (r as any).materia || "").toLowerCase().includes("laboral"));
                props.onLaboralesDetected?.(hasLaboral);
            } else {
                console.error("Error fetching audiencias records:", response.statusText);
                setRecords([]);
            }
        } catch (error) {
            console.error("Error fetching audiencias records:", error);
            setRecords([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            bindRecords();
        }
    }, [id]);

    // Group records by nuc and get oldest and newest for each group.
    // Robust: try multiple fields for nuc and date, parse dates with moment,
    // then sort groups by oldest date (oldest -> newest) and flatten.
    const groupedRecords = useMemo(() => {
        if (records.length === 0) return [];

        const getNucKey = (r: any) => r.nuc || r.noUnico || r.noExpediente || r.noUnico || "N/A";
        const getTimestamp = (r: any) => {
            const candidate = r.fechaDate || r.fechaAudiencia || r.fecha || r.fechaAudienciaString || "";
            const m = moment(candidate);
            return m.isValid() ? m.valueOf() : 0;
        };

        // Group by nuc key
        const grouped = records.reduce((acc: { [key: string]: any[] }, record: any) => {
            const key = getNucKey(record);
            if (!acc[key]) acc[key] = [];
            acc[key].push(record);
            return acc;
        }, {} as { [key: string]: any[] });

        // Build groups with oldest timestamp for sorting
        const groups = Object.keys(grouped).map((nuc) => {
            const groupRecords = grouped[nuc] || [];
            const sorted = [...groupRecords].sort((a: any, b: any) => getTimestamp(a) - getTimestamp(b));

            const items: any[] = [];
            if (sorted.length > 0) {
                const first = { ...sorted[0] };
                // ensure fechaDate exists for display
                if (!first.fechaDate) {
                    const candidate = first.fechaAudiencia || first.fecha || "";
                    first.fechaDate = moment(candidate).isValid() ? moment(candidate).toISOString() : first.fechaDate;
                }
                items.push({ ...first, isOldest: true });
            }
            if (sorted.length > 1) {
                const last = { ...sorted[sorted.length - 1] };
                if (!last.fechaDate) {
                    const candidate = last.fechaAudiencia || last.fecha || "";
                    last.fechaDate = moment(candidate).isValid() ? moment(candidate).toISOString() : last.fechaDate;
                }
                items.push({ ...last, isNewest: true });
            }

            const oldestTs = sorted.length > 0 ? getTimestamp(sorted[0]) : 0;

            return { nuc, oldestTs, items };
        });

        // Sort groups by oldest timestamp (ascending = oldest -> newest)
        groups.sort((a, b) => a.oldestTs - b.oldestTs);

        // Flatten groups preserving oldest then newest order per group
        // and add groupIndex/withinIndex for enumeration like 1.1, 1.2
        const flattened: any[] = [];
        groups.forEach((g, gi) => {
            g.items.forEach((it, wi) => {
                // normalize nuc for display
                const displayNuc = it.nuc || g.nuc || getNucKey(it);
                flattened.push({ ...it, nuc: displayNuc, groupIndex: gi + 1, withinIndex: wi + 1 });
            });
        });

        return flattened;
    }, [records]);

    const renderPartesHTML = (partes: any) => {
        if (!partes) return "-";
        const text = typeof partes === "string" ? partes : String(partes);
        const highlightRegex = /(demandado|accionado|recurrido|imputado|condenado)/i;
        // Split into segments by comma or semicolon and mark segments that contain target labels
        const segments = text.split(/[,;]+/);
        const processed = segments
            .map((seg) => {
                const s = seg.trim();
                if (highlightRegex.test(s)) {
                    return `<span class="text-danger">${s}</span>`;
                }
                return s;
            })
            .filter(Boolean);
        return processed.join(", ");
    };

    const roleBadgeClass = (rol?: string) => {
        const r = (rol || "").toLowerCase();
        if (/imputad|investigad|condenad|acusad/.test(r)) return "bg-danger";
        if (/querellante|v[ií]ctima|demandante|accionante/.test(r)) return "bg-primary";
        if (/abogad/.test(r)) return "bg-secondary";
        return "bg-success";
    };

    const renderPartesStructured = (item: any) => {
        const list: ParteItem[] = Array.isArray(item?.partesList) ? item.partesList : [];
        if (list.length === 0) {
            // Fallback: legacy text rendering when no structured data is available.
            return <span dangerouslySetInnerHTML={{ __html: renderPartesHTML(item?.partes || item?.involucrados) }} />;
        }
        return (
            <div className="d-flex flex-column gap-2 mt-2">
                {list.map((p, idx) => {
                    const status = p.cedulaMatchStatus ?? 0;
                    const matched = status === 1 || status === 4 || status === 7;
                    const entity = status === 5;
                    const ambiguous = status === 2 || status === 6;
                    const hasId = !!p.identificador;
                    return (
                        <div key={p.id ?? idx} className="d-flex align-items-center flex-wrap gap-2 border-start ps-2" style={{ borderWidth: 3 }}>
                            <span className={`badge ${roleBadgeClass(p.rol)}`}>{p.rol || t("Parte")}</span>
                            <span className="fw-semibold">{p.nombre}</span>
                            {hasId && (matched || entity) ? (
                                <a
                                    href={`/bcr/${p.identificador}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="badge bg-light text-dark border text-decoration-none"
                                    title={String(entity ? t("Empresa / RNC") : t("Buscar esta cédula"))}
                                >
                                    <i className={`fa ${entity ? "fa-building" : "fa-id-card"} me-1`}></i>
                                    {p.identificador}
                                    <i className="fa fa-arrow-up-right-from-square ms-1"></i>
                                </a>
                            ) : ambiguous ? (
                                <span className="badge bg-warning text-dark" title={String(t("Varios candidatos, sin asignar"))}>
                                    <i className="fa fa-triangle-exclamation me-1"></i>
                                    {p.cedulaMatchCandidates ?? 0} {t("candidatos")}
                                </span>
                            ) : status === 0 ? (
                                <span className="badge bg-primary text-white">
                                    <i className="fa fa-clock me-1"></i>{t("Procesando")}
                                </span>
                            ) : (
                                <span className="badge bg-light text-muted border">
                                    <i className="fa fa-circle-question me-1"></i>{t("Sin cédula")}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    if (isLoading) {
        return (
            <Card className="my-4">
                <Card.Body className="text-center py-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (records.length === 0) {
        return null;
    }

    return (
        <Card className="my-4">
            <Card.Header className="display-4">
                {t("Procesos Judiciales")}
                {/* <Badge bg="warning" className="ms-3">
                    {groupedRecords.length}
                </Badge> */}
            </Card.Header>
            <Card.Body className="p-0">
                {viewMode === "table" && (
                    <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t("Expediente")}</th>
                                    <th>{t("Tribunal")}</th>
                                    <th>{t("Materia")}</th>
                                    <th>{t("Fecha y Hora")}</th>
                                    {/* <th>{t("Tipo")}</th>
                                <th>{t("Estado")}</th> */}
                                    <th>{t("Partes")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedRecords.map((item: any) => {
                                    console.log("Rendering audiencia item:", item);
                                    return (
                                        <tr key={`${item.nuc}-${item.groupIndex}-${item.withinIndex}`} className={item.isNewest ? 'table-success' : item.isOldest ? 'table-warning' : ''}>
                                            <td>{`${item.groupIndex}.${item.withinIndex}`}</td>
                                            <td>
                                                {item.nuc}
                                            </td>
                                            <td className="text-wrap">
                                                {item.tribunal}
                                                {item.sala && item.sala !== "N/D" && (
                                                    <>
                                                        <br />
                                                        <small className="text-muted">
                                                            {item.categoriaTribunal}
                                                        </small>
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                {item.materia && item.materia !== "N/D"
                                                    ? item.materia == "PENAL" ? <span className="text-danger font-weight-bold">{item.materia}</span> : <span className="text-success">{item.materia}</span>
                                                    : "-"}
                                                {item.estadoAudiencia && /rebeld/i.test(item.estadoAudiencia) && (
                                                    <Badge bg="danger" className="ms-1 d-block">Rebeldía</Badge>
                                                )}
                                            </td>
                                            <td>
                                                {item.fechaDate ? (
                                                    <>
                                                        <strong>
                                                            {moment(item.fechaDate).format("DD/MM/YYYY")}
                                                        </strong>
                                                        <br />
                                                        <small>{moment(item.fechaDate).format("HH:mm")}</small>
                                                    </>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            {/* <td>
                                            {item.estadoRolAudiencia && item.estadoRolAudiencia !== "N/D"
                                                ? item.estadoRolAudiencia
                                                : "-"}
                                        </td>
                                        <td>
                                            {item.tipoResultado}
                                        </td> */}
                                            <td className="text-wrap">
                                                {renderPartesStructured(item)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>)}
                {viewMode === "conversation" && (
                    <div className="list-group">
                        {groupedRecords.map((item: any) => {
                            console.log("Rendering audiencia item:", item);
                            return (
                                <div key={`${item.nuc}-${item.groupIndex}-${item.withinIndex}`} className={`list-group-item ${item.isNewest ? 'list-group-item-success' : item.isOldest ? 'list-group-item-warning' : ''}`}>
                                    <div>
                                        <strong className="me-2">{`${item.groupIndex}.${item.withinIndex}`}</strong>
                                        <span dangerouslySetInnerHTML={{ __html: `Según al expediente no. <b>${item.nuc}</b> del tribunal <b>${item.tribunal}</b> de fecha <b>${moment(item.fechaDate).format("DD/MM/YYYY")}</b> figuran entre las partes:` }} />
                                        {(() => {
                                            const m = (item.materia || "").toLowerCase();
                                            const badgeClass = m === "penal" ? "badge bg-danger" : m === "laboral" ? "badge bg-warning text-dark" : m === "civil" ? "badge bg-primary" : "badge bg-success";
                                            return <span className={`${badgeClass} ms-1`}>{item.materia}</span>;
                                        })()}
                                        {item.estadoRolAudiencia && (
                                            <span className={`badge ms-1 ${/conocid/i.test(item.estadoRolAudiencia) ? "bg-secondary text-white" : "bg-primary text-white"}`}>
                                                {item.estadoRolAudiencia}
                                            </span>
                                        )}
                                        {renderPartesStructured(item)}
                                    </div>
                                    {item.estadoAudiencia && /rebeld/i.test(item.estadoAudiencia) && (
                                        <Badge bg="danger" className="ms-1">Rebeldía</Badge>
                                    )}
                                    {item.isOldest && <small className="text-muted d-block mt-1"><i className="fa fa-clock me-1"></i>{t("Registro más antiguo")}</small>}
                                    {item.isNewest && <small className="text-muted d-block mt-1"><i className="fa fa-clock me-1"></i>{t("Registro más reciente")}</small>}
                                </div>)
                        })}
                    </div>)}
            </Card.Body>
            <Card.Footer className="text-muted">
                <small>
                    <i className="fa fa-info-circle me-2"></i>
                    {t("Total de imputaciones encontradas")}: {records.length} ({t("Mostrando")}: {groupedRecords.length})
                </small>
            </Card.Footer>
        </Card>
    );
};

export default RecordAudiencias;
